/* eslint-disable max-len */
import tempMeeApi from '../apis/tempMee';

export const getCredentialsV2 = async (userId, userType) =>
  tempMeeApi.get(`credential-service/users/${userId}/credentials`, {
    params: {
      userType,
    },
  });

export const patchCredentials = async (userId, education, insurances) =>
  tempMeeApi.patch(`admin/users/${userId}/specialtiesAndProcedures`, { education, insurances });

export const patchCredentialsV2 = async (userId, userType, education, insurances, softwares) =>
  tempMeeApi.patch(`credential-service/users/${userId}/credentials?userType=${userType}`, {
    education,
    insurances,
    ...(softwares && { softwares }),
  });

/** get prof licenses from monolith with secure s3 image url */
export const getProtectedProfLicenses = async (userId) =>
  tempMeeApi.get(`admin/users/${userId}/licenses`);

export const getProfessionalAttestation = async (userId, code) =>
  tempMeeApi.get(`credential-service/users/${userId}/consents/${code}`);

export const getDentistAttestation = async (officeId, code) =>
  tempMeeApi.get(`credential-service/admin/consents/${code}`, {
    params: {
      office_id: officeId,
    },
  });

export const updateDentistAttestation = async ({ officeId, code, isEnabled }) =>
  tempMeeApi.patch(
    `credential-service/admin/consents/${code}`,
    { is_enabled: isEnabled },
    {
      params: {
        office_id: officeId,
      },
    },
  );

export const getAuthorizations = async (professionalId) =>
  tempMeeApi.get(`credential-service/professionals/${professionalId}/authorizations`);

export const deleteAuthorization = async (professionalId, authorizationId) =>
  tempMeeApi.delete(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}`,
  );

export const createAuthorization = async (professionalId, { state, profession }) =>
  tempMeeApi.put(`credential-service/professionals/${professionalId}/authorizations`, {
    state,
    profession,
  });

export const getMonitoredCredentials = async (professionalId) =>
  tempMeeApi.get(`credential-service/admin/professionals/${professionalId}/monitored-credentials`);

/* eslint-disable camelcase, no-underscore-dangle */
// --- V2 credential update APIs ---

export const updateCredentialArtifact = async (
  professionalId,
  credentialId,
  { credential_type, issue_date, expire_date, status, admin_decision_reason, files },
) => {
  const formData = new FormData();

  if (credential_type) {
    formData.append('credential_type', credential_type);
  }
  if (issue_date) {
    formData.append('issue_date', issue_date);
  }
  if (expire_date !== undefined) {
    formData.append('expire_date', expire_date ?? '');
  }
  if (status) {
    formData.append('status', status);
  }
  // Admin-only. Presence matters: an empty value clears the stored reason, an absent
  // field leaves it untouched (GOTU-19426).
  if (typeof admin_decision_reason === 'string') {
    formData.append('admin_decision_reason', admin_decision_reason);
  }

  if (files?.length) {
    files.forEach((file) => {
      if (file._local && file._file) {
        formData.append('files', file._file);
      }
    });
  }

  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/artifact/${credentialId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const updateCredentialNumber = async (professionalId, credentialId, data) =>
  tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/number/${credentialId}`,
    data,
  );

export const updateCredentialReference = async (professionalId, credentialId, data) =>
  tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/reference/${credentialId}`,
    data,
  );

export const updateCredentialNpi = async (professionalId, credentialId, data) =>
  tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/npi/${credentialId}`,
    data,
  );
// --- V2 credential create APIs ---

export const createCredentialArtifact = async (
  professionalId,
  authorizationId,
  { credential_type, issue_date, expire_date, status, files },
) => {
  const formData = new FormData();

  if (credential_type) {
    formData.append('credential_type', credential_type);
  }
  if (issue_date) {
    formData.append('issue_date', issue_date);
  }
  if (expire_date !== undefined) {
    formData.append('expire_date', expire_date ?? '');
  }
  if (status) {
    formData.append('status', status);
  }

  if (files?.length) {
    files.forEach((file) => {
      if (file._local && file._file) {
        formData.append(`files`, file._file);
      }
    });
  }

  return tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/artifact`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const createCredentialNumber = async (professionalId, authorizationId, data) =>
  tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/number`,
    data,
  );

export const createCredentialReference = async (professionalId, authorizationId, data) =>
  tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/reference`,
    data,
  );

export const createCredentialNpi = async (professionalId, authorizationId, data) =>
  tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/npi`,
    data,
  );
// Admin CS-confirmed name (per state). Multipart so name + docs can go together.
// files === undefined  -> field omitted -> backend preserves existing docs (names-only update)
// files === array      -> local blobs are re-uploaded, replacing the doc set
// ponytail: delete-all-docs-without-replacement isn't expressible over multipart (no empty
// files part), so it falls back to preserve; add a `clear_files` flag if CS needs it.
export const updateAdminConfirmedName = async (
  professionalId,
  { state, first_name, last_name, files },
) => {
  const formData = new FormData();
  formData.append('state', state);
  if (first_name !== undefined) {
    formData.append('firstName', first_name ?? '');
  }
  if (last_name !== undefined) {
    formData.append('lastName', last_name ?? '');
  }
  if (files) {
    files.forEach((file) => {
      if (file._local && file._file) {
        formData.append('files', file._file);
      }
    });
  }

  return tempMeeApi.put(`credential-service/admin/confirmed-names/${professionalId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getCredentialAuditTrail = async (professionalId) =>
  tempMeeApi.get(`credential-service/professionals/${professionalId}/credential-audit-trail`);
/* eslint-enable camelcase, no-underscore-dangle */
